import { OASElement } from '@oas-ui/core'
// 图标渲染走 @oas-ui/icons 运行时查表（registerIcon 自定义优先、内置集兜底，与 oas-icon 同源），
// 不经 oas-icon 组件模块——避免把整个 <oas-icon> 拉进 sidebar 按需链
import { lookupIcon } from '@oas-ui/icons/runtime'
import { isRtl } from '../../shared/direction.js'
import { computePosition, getViewport } from '../../overlay/floating/index.js'

/** 菜单项操作按钮：悬停项时出现，点击派发 `oas-action` */
export interface SidebarItemAction {
  icon?: string
  value: string
  label: string
}

/** 菜单项：label 必填；value 用于 `oas-select` 事件；icon 在折叠态显示（可选） */
export interface SidebarItem {
  label: string
  value: string
  icon?: string
  /** 图标颜色（可选）：显式时固定该色，优先于禁用/激活态默认色；任意 CSS 色值 */
  iconColor?: string
  /** 分组名（可选）：连续同组项在组首项前渲染组标题节点（纯展示、不可点） */
  group?: string
  /** 徽标计数（可选）：项右侧小圆角徽标 */
  badge?: string | number
  /** 嵌套子菜单（可选）：有 children 的项渲染为可展开/收起的父项 */
  children?: SidebarItem[]
  /** 项操作按钮（可选）：悬停项时出现在右侧 */
  actions?: SidebarItemAction[]
}

/** 分隔线条目：`{type:'divider'}` 在菜单中渲染分隔线 */
export interface SidebarDivider {
  type: 'divider'
}

export type SidebarEntry = SidebarItem | SidebarDivider

/** 默认移动端断点（px，窄于此视口宽度时抽屉化） */
const DEFAULT_BREAKPOINT = 768

const STYLE = `
:host {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: var(--oas-sidebar-width, 220px);
  height: 100%;
  min-width: 0;
  background: var(--oas-sidebar-bg, var(--oas-color-bg-hover));
  font-family: inherit;
  transition: width var(--oas-transition-base, 180ms) var(--oas-ease-out, cubic-bezier(0.2, 0, 0.2, 1));
}
:host([hidden]) {
  display: none;
}
:host([collapsed]) {
  width: var(--oas-sidebar-collapsed-width, 64px);
}
/* 在 oas-sider 轨道内：填满轨道（宽度契约「sider 管轨道、sidebar 填满」）。
   :host-context(oas-sider) 特异性 (0,1,1) 高于 :host 默认宽 (0,1,0) 稳覆盖；
   低于 [collapsed]/[data-mobile] (0,2,0)，折叠态与移动抽屉态不受影响 */
:host-context(oas-sider) {
  width: 100%;
}
/* 移动端：宿主不占布局空间（抽屉/触发按钮均为 fixed 定位） */
:host([data-mobile]) {
  width: auto;
  height: auto;
  background: none;
}
aside {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
  overflow-y: auto;
}
/* 移动端：面板变覆盖式抽屉（inset-inline-start 逻辑属性，RTL 自动对齐） */
:host([data-mobile]) .panel {
  position: fixed;
  inset-block: 0;
  inset-inline-start: 0;
  width: min(var(--oas-sidebar-width, 220px), 80vw);
  max-width: 80vw;
  background: var(--oas-color-bg);
  /* 方向性投影：投影侧随停靠侧（placement 物理语义） */
  box-shadow: 4px 0 16px rgba(0, 0, 0, 0.12);
  z-index: calc(var(--oas-z-index-base, 0) + var(--oas-z-overlay, 1040));
  visibility: hidden;
  transform: translateX(-100%);
  transition:
    transform var(--oas-transition-base, 180ms) var(--oas-ease-out, cubic-bezier(0.2, 0, 0.2, 1)),
    visibility 0s var(--oas-transition-base, 180ms);
}
/* RTL：抽屉贴视觉右缘（inset-inline-start 逻辑镜像），关闭态藏到右外（transform 物理向量手动镜像） */
:host([data-rtl]) .panel {
  transform: translateX(100%);
  /* 方向性投影：投影侧随停靠侧（placement 物理语义） */
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.12);
}
:host([data-mobile]) .panel.drawer-open {
  visibility: visible;
  transform: translateX(0);
  transition:
    transform var(--oas-transition-base, 180ms) var(--oas-ease-out, cubic-bezier(0.2, 0, 0.2, 1)),
    visibility 0s;
}
/* 移动端遮罩 */
.mask {
  position: fixed;
  inset: 0;
  background: var(--oas-color-overlay);
  z-index: calc(calc(var(--oas-z-index-base, 0) + var(--oas-z-overlay, 1040)) - 1);
  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--oas-transition-base, 180ms) var(--oas-ease-out, cubic-bezier(0.2, 0, 0.2, 1)),
    visibility 0s var(--oas-transition-base, 180ms);
}
.mask.open {
  opacity: 1;
  visibility: visible;
  transition:
    opacity var(--oas-transition-base, 180ms) var(--oas-ease-out, cubic-bezier(0.2, 0, 0.2, 1)),
    visibility 0s;
}
/* 桌面端隐藏移动态元素 */
:host(:not([data-mobile])) .mask,
:host(:not([data-mobile])) [part='trigger'] {
  display: none;
}
:host([data-mobile]) [part='toggle'] {
  display: none;
}
/* 桌面折叠：只显示图标，隐藏头/体/脚与菜单 label */
:host(:not([data-mobile])[collapsed]) .head,
:host(:not([data-mobile])[collapsed]) .body,
:host(:not([data-mobile])[collapsed]) .foot {
  display: none;
}
:host(:not([data-mobile])[collapsed]) .item {
  justify-content: center;
  padding-inline: 0;
  /* 徽标角标定位锚点 */
  position: relative;
}
:host(:not([data-mobile])[collapsed]) .item .label {
  display: none;
}
/* 折叠态徽标：改为图标右上角紧凑角标（绝对定位不占布局、不撑出 64px 图标条；
   默认药丸内联布局在窄条里会溢出成白条） */
:host(:not([data-mobile])[collapsed]) .item-badge {
  position: absolute;
  inset-block-start: 2px;
  inset-inline-end: 2px;
  margin-inline-start: 0;
  min-width: 14px;
  height: 14px;
  padding: 0 2px;
  border-radius: 7px;
  font-size: 9px;
  line-height: 14px;
  /* 深底浅字强对比（角标在图标上方，浅底小字会糊） */
  background: var(--oas-sidebar-badge-bg, var(--oas-color-primary));
  color: var(--oas-sidebar-badge-color, #fff);
}
/* 头 / 体 / 脚 */
.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--oas-space-2, 8px);
  padding: var(--oas-space-3, 12px);
}
[part='close'] {
  border: none;
  background: none;
  padding: var(--oas-space-1, 4px);
  color: var(--oas-color-text-secondary);
  cursor: pointer;
  font-size: var(--oas-font-size-md, 14px);
  font-family: inherit;
}
[part='close']:hover {
  color: var(--oas-color-text-primary);
}
.body {
  padding: var(--oas-space-3, 12px);
}
.foot {
  margin-top: auto;
  padding: var(--oas-space-3, 12px);
}
/* 菜单 */
.nav {
  display: flex;
  flex-direction: column;
  gap: var(--oas-space-1, 4px);
  padding: var(--oas-space-2, 8px);
}
.item {
  display: flex;
  align-items: center;
  gap: var(--oas-space-3, 12px);
  width: 100%;
  min-height: var(--oas-control-height-lg, 40px);
  padding: 0 var(--oas-space-3, 12px);
  border: none;
  background: none;
  border-radius: var(--oas-radius-md, 6px);
  cursor: pointer;
  color: var(--oas-color-text-primary);
  font-size: var(--oas-font-size-md, 14px);
  font-family: inherit;
  white-space: nowrap;
  text-align: start;
}
.item:hover {
  background: var(
    --oas-sidebar-item-hover-bg,
    color-mix(in srgb, var(--oas-color-text-primary) 6%, transparent)
  );
}
.item.active {
  background: color-mix(in srgb, var(--oas-color-primary) 14%, transparent);
  color: var(--oas-color-primary-text);
  font-weight: 500;
}
/* active 项 hover 也有反馈：主色浅调加深一档（原与常态同色调，hover 无变化） */
.item.active:hover {
  background: color-mix(in srgb, var(--oas-color-primary) 20%, transparent);
}
.item.active .icon {
  color: var(--oas-color-primary-text);
}
/* child-selected：激活后代的父项指示（比 active 浅一档；折叠图标条下子项隐身，
   激活态经父项图标主色保留可见；父项非当前页，不加粗不设 aria-current） */
.item.child-selected {
  background: color-mix(in srgb, var(--oas-color-primary) 10%, transparent);
}
.item.child-selected .icon {
  color: var(--oas-color-primary-text);
}
.item[hidden] {
  display: none;
}
.item .icon {
  flex-shrink: 0;
  width: var(--oas-control-height-md, 32px);
  text-align: center;
  font-size: var(--oas-font-size-lg, 16px);
  color: var(--oas-color-text-secondary);
}
.item .label {
  overflow: hidden;
  text-overflow: ellipsis;
}
/* 组标题：弱化小标题，纯展示不可点；折叠态隐藏（同 .item .label） */
.group-title {
  padding: var(--oas-space-2, 8px) var(--oas-space-3, 12px) var(--oas-space-1, 4px);
  color: var(--oas-color-text-secondary);
  font-size: var(--oas-font-size-xs, 12px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
:host(:not([data-mobile])[collapsed]) .group-title {
  display: none;
}
/* 徽标：项右侧小圆角计数（色值走 --oas-sidebar-badge-* 开口，默认主色浅调） */
.item-badge {
  flex-shrink: 0;
  margin-inline-start: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  box-sizing: border-box;
  padding: 0 var(--oas-space-1, 4px);
  border-radius: 9px;
  background: var(--oas-sidebar-badge-bg, color-mix(in srgb, var(--oas-color-primary) 14%, transparent));
  color: var(--oas-sidebar-badge-color, var(--oas-color-primary-text));
  font-size: var(--oas-font-size-xs, 12px);
  line-height: 18px;
  white-space: nowrap;
}
/* 嵌套父项：展开箭头（展开时旋转） */
.chevron {
  flex-shrink: 0;
  margin-inline-start: auto;
  display: inline-flex;
  color: var(--oas-color-text-secondary);
  transition: transform var(--oas-transition-fast, 120ms) var(--oas-ease-out);
}
.item[aria-expanded='true'] .chevron {
  transform: rotate(90deg);
}
.item-badge + .chevron {
  margin-inline-start: var(--oas-space-2, 8px);
}
/* 嵌套子菜单容器：缩进 + 左侧引导线（类名 .submenu 与嵌套按钮 .item.sub 区分，避免样式串扰）。
   展开/收起平滑动画：grid-template-rows 0fr↔1fr 过渡（免 JS 测量），visibility 联动
   （收起时出渲染树/无障碍树/防聚焦——替代 display:none 的语义兜底，但可过渡） */
.submenu {
  display: grid;
  grid-template-rows: 0fr;
  visibility: hidden;
  margin-inline-start: calc(var(--oas-control-height-md, 32px) / 2 + var(--oas-space-3, 12px));
  /* 缩进收敛（实测评估）：容器 padding 12→6，配合子项图标占位 24→18，
     子项 label 距引导线 61→49px——无图标子项不再显空肥，层级仍清晰 */
  padding-inline-start: var(--oas-space-1_5, 6px);
  /* 引导线对比度：text-primary 12% 混色（亮暗双主题自适应） */
  border-inline-start: 1px solid color-mix(in srgb, var(--oas-color-text-primary) 12%, transparent);
  transition:
    grid-template-rows var(--oas-transition-base, 180ms) var(--oas-ease-out, cubic-bezier(0.2, 0, 0.2, 1)),
    visibility 0s linear var(--oas-transition-base, 180ms);
}
.submenu:not([hidden]) {
  grid-template-rows: 1fr;
  visibility: visible;
  transition:
    grid-template-rows var(--oas-transition-base, 180ms) var(--oas-ease-out, cubic-bezier(0.2, 0, 0.2, 1)),
    visibility 0s;
}
.submenu-inner {
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: var(--oas-space-1, 4px);
}
@media (prefers-reduced-motion: reduce) {
  .submenu,
  .submenu:not([hidden]) {
    transition: none;
  }
}
/* 父项与子树容器：btn 与 submenu 之间留呼吸——无 gap 时父项底色（active/child-selected）
   与子项 hover 底色上下贴死「黏在一起」（实测观感） */
.item-block {
  display: flex;
  flex-direction: column;
  gap: var(--oas-space-1, 4px);
}
/* ===== 折叠图标条态的子菜单 flyout（collapsed × 树形 children）=====
   父项不是页面（点击开合面板），子项才是导航目标；面板 fixed 定位、定位引擎锚定父项
   inline-end（RTL 镜像到左）、碰撞避让。hover 延迟开合 + Esc/外部点击关闭。 */
.flyout {
  position: fixed;
  z-index: calc(var(--oas-z-index-base, 0) + var(--oas-z-overlay, 1040));
  min-width: 180px;
  max-width: 260px;
  background: var(--oas-color-bg-elevated);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  box-shadow: var(--oas-shadow-lg);
  padding: var(--oas-space-1);
  display: flex;
  flex-direction: column;
  gap: var(--oas-space-1, 4px);
  max-height: 70vh;
  overflow-y: auto;
}
.flyout[hidden] {
  display: none;
}
/* flyout 内子项占满宽度（视觉对齐弹层惯例）；折叠态 .item 布局规则
   （:host(:not([data-mobile])[collapsed]) .item，0,4,0）会误伤 flyout 内子项——
   选择器带完整 :host 前缀（0,5,0）压过折叠态规则 */
:host(:not([data-mobile])[collapsed]) .flyout .item {
  width: 100%;
  justify-content: flex-start;
  height: var(--oas-control-height-md);
  padding: 0 var(--oas-space-2);
}
:host(:not([data-mobile])[collapsed]) .flyout .item .label {
  display: inline;
}
@media (prefers-reduced-motion: reduce) {
  .flyout {
    transition: none;
  }
}
.item.sub .icon {
  width: 18px;
}
/* 项操作按钮：默认隐藏，悬停/聚焦项时出现 */
.item-actions {
  flex-shrink: 0;
  margin-inline-start: auto;
  display: none;
  align-items: center;
  gap: var(--oas-space-1, 4px);
}
.item:hover .item-actions,
.item:focus-within .item-actions {
  display: inline-flex;
}
.item-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: var(--oas-radius-sm, 4px);
  background: none;
  color: var(--oas-color-text-secondary);
  cursor: pointer;
  font-size: var(--oas-font-size-sm, 13px);
  font-family: inherit;
}
.item-action:hover {
  background: var(--oas-color-bg-hover);
  color: var(--oas-color-text-primary);
}
/* 分隔线 */
.divider {
  height: 1px;
  margin: var(--oas-space-2, 8px) var(--oas-space-3, 12px);
  background: var(--oas-color-border);
}
/* 边缘拖拽条（resizable）：贴宿主右缘，桌面态显示；折叠/移动态隐藏 */
[part='rail'] {
  position: absolute;
  inset-block: 0;
  inset-inline-end: 0;
  width: 6px;
  margin-inline-end: -3px;
  cursor: col-resize;
  touch-action: none;
  z-index: 1;
}
[part='rail']::after {
  content: '';
  position: absolute;
  inset-block: 0;
  inset-inline-start: 2px;
  width: 2px;
  background: transparent;
  border-radius: 2px;
  transition: background var(--oas-transition-fast, 120ms) var(--oas-ease-out);
}
[part='rail']:hover::after,
[part='rail']:focus-visible::after,
:host([data-resizing]) [part='rail']::after {
  background: var(--oas-color-primary);
}
:host(:not([data-mobile])[collapsed]) [part='rail'],
:host([data-mobile]) [part='rail'] {
  display: none;
}
:host {
  position: relative;
}
:host([data-resizing]) {
  user-select: none;
  transition: none;
}
/* 骨架屏：loading 态脉冲骨架行（与宿主 bg-hover 底色区分开，用主文字色低透明度混色） */
.skeleton {
  height: var(--oas-control-height-lg, 40px);
  border-radius: var(--oas-radius-md, 6px);
  background: color-mix(in srgb, var(--oas-color-text-primary) 9%, transparent);
  animation: oas-sidebar-pulse 1.2s var(--oas-ease-out, ease-in-out) infinite;
}
@keyframes oas-sidebar-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}
/* expand-on-hover：折叠图标条悬停临时展开（视觉态，不改 collapsed 受控） */
:host(:not([data-mobile])[collapsed][expand-on-hover]:hover) {
  width: var(--oas-sidebar-width, 220px);
  position: relative;
  z-index: calc(var(--oas-z-dropdown, 1000) + 1);
  /* 方向性投影：投影侧随停靠侧（placement 物理语义） */
  box-shadow: 4px 0 16px rgba(0, 0, 0, 0.12);
}
:host([data-rtl]:not([data-mobile])[collapsed][expand-on-hover]:hover) {
  /* 方向性投影：投影侧随停靠侧（placement 物理语义） */
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.12);
}
:host(:not([data-mobile])[collapsed][expand-on-hover]:hover) .item .label,
:host(:not([data-mobile])[collapsed][expand-on-hover]:hover) .group-title {
  display: inline;
}
:host(:not([data-mobile])[collapsed][expand-on-hover]:hover) .group-title {
  display: block;
}
:host(:not([data-mobile])[collapsed][expand-on-hover]:hover) .item {
  justify-content: flex-start;
  padding-inline: var(--oas-space-3, 12px);
}
:host(:not([data-mobile])[collapsed][expand-on-hover]:hover) .item[hidden] {
  display: flex;
}
/* side=right：移动抽屉从行内末端滑入、触发按钮在行内末端（逻辑映射，RTL 自动换边） */
:host([data-mobile][side='right']) .panel {
  inset-inline-start: auto;
  inset-inline-end: 0;
  transform: translateX(100%);
  /* 方向性投影：投影侧随停靠侧（placement 物理语义） */
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.12);
}
:host([data-mobile][side='right']) .panel.drawer-open {
  transform: translateX(0);
}
:host([data-mobile][side='right']) [part='trigger'] {
  inset-inline-start: auto;
  inset-inline-end: var(--oas-space-4, 16px);
}
/* side=right + RTL：抽屉贴视觉左缘，关闭态藏到左外 */
:host([data-mobile][side='right'][data-rtl]) .panel {
  transform: translateX(-100%);
  /* 方向性投影：投影侧随停靠侧（placement 物理语义） */
  box-shadow: 4px 0 16px rgba(0, 0, 0, 0.12);
}
:host([data-mobile][side='right'][data-rtl]) .panel.drawer-open {
  transform: translateX(0);
}
/* variant=floating：悬浮形态（外边距 + 圆角 + 阴影） */
:host([variant='floating']) {
  margin: var(--oas-space-3, 12px);
  height: calc(100% - var(--oas-space-3, 12px) * 2);
  border-radius: var(--oas-radius-lg, 10px);
  background: var(--oas-sidebar-bg, var(--oas-color-bg));
  box-shadow: var(--oas-shadow-md);
  overflow: hidden;
}
/* variant=inset：内嵌形态（外边距 + 圆角 + 背景对比） */
:host([variant='inset']) {
  margin: var(--oas-space-3, 12px);
  height: calc(100% - var(--oas-space-3, 12px) * 2);
  border-radius: var(--oas-radius-lg, 10px);
  background: var(--oas-sidebar-bg, var(--oas-color-bg-hover));
  overflow: hidden;
}
:host([variant='floating']) aside,
:host([variant='inset']) aside {
  border-radius: inherit;
}
/* 折叠切换按钮 */
[part='toggle'] {
  flex-shrink: 0;
  height: var(--oas-control-height-lg, 40px);
  border: none;
  border-top: 1px solid var(--oas-color-border);
  background: none;
  color: var(--oas-color-text-secondary);
  cursor: pointer;
  font-size: var(--oas-font-size-sm, 13px);
  font-family: inherit;
}
[part='toggle']:hover {
  background: var(--oas-color-bg-hover);
  color: var(--oas-color-text-primary);
}
/* 移动端抽屉触发按钮（固定悬浮，在遮罩层级之下） */
[part='trigger'] {
  position: fixed;
  top: var(--oas-space-4, 16px);
  inset-inline-start: var(--oas-space-4, 16px);
  width: var(--oas-control-height-lg, 40px);
  height: var(--oas-control-height-lg, 40px);
  border: none;
  border-radius: var(--oas-radius-md, 6px);
  background: var(--oas-color-bg-elevated);
  color: var(--oas-color-text-primary);
  font-size: var(--oas-font-size-lg, 16px);
  font-family: inherit;
  cursor: pointer;
  box-shadow: var(--oas-shadow-sm);
  z-index: calc(calc(var(--oas-z-index-base, 0) + var(--oas-z-overlay, 1040)) - 1);
}
[part='trigger']:hover {
  background: var(--oas-color-bg-hover);
}
@media (prefers-reduced-motion: reduce) {
  :host,
  .panel,
  .mask {
    transition: none;
  }
}
/* ===== 粗指针触控目标 xl 档 48px（移动端基线；细指针桌面保持 lg 档不变；.item.sub 子项同类名一并覆盖） ===== */
@media (pointer: coarse) {
  .item {
    min-height: var(--oas-control-height-xl, 48px);
  }
}
`

/**
 * oas-sidebar —— 与 layout/sider 对齐的可折叠侧栏。
 *
 * 属性（kebab-case）：
 * - `collapsed`：受控折叠，收窄为图标条（无 icon 的菜单项隐藏）
 * - `accordion`：手风琴互斥展开（同级只留一个展开的子菜单）
 * - `items`：菜单 JSON `[{label, value, icon?, iconColor?, group?, badge?, children?, actions?}]`，支持分隔项
 * - `active`：当前激活菜单项 value（受控，外部赋值/点击均可驱动高亮迁移与重绘）
 * - `width`：展开宽度，默认走 `--oas-sidebar-width` token
 * - `mobile-breakpoint`：移动端断点（px，默认 768），窄于此视口时变覆盖式抽屉
 * - `drawer-open`：移动端抽屉态（受控，Esc/遮罩点击自动移除）
 * - `hide-toggle`：隐藏底部折叠按钮
 * - `loading`：骨架加载态，值为骨架行数（默认 4）
 * - `expand-on-hover`：折叠图标条悬停时临时展开（视觉态，不改 collapsed 受控值）
 * - `shortcut`：启用 Ctrl/Cmd+B 折叠切换快捷键
 * - `side`：停靠侧 left（默认）/ right
 * - `variant`：形态 sidebar（默认，通栏）/ floating（悬浮圆角投影）/ inset（内嵌圆角边框）
 * - `resizable`：右/左边缘拖拽调宽（折叠态禁用）
 * - `resize-min` / `resize-max`：拖拽宽度上下限（px，默认 160 / 480）
 *
 * 事件：
 * - `oas-collapse`：detail `{ collapsed }`，折叠切换时派发（含快捷键）
 * - `oas-select`：detail `{ value, label }`，选中菜单项时派发（受控迁移同时派发）
 * - `oas-action`：detail `{ value, action, label }`，菜单项操作按钮点击时派发
 * - `oas-resize`：detail `{ width }`，拖拽松手 / 复位微调结束时派发
 *
 * 移动端抽屉：遮罩 + 点击外部/关闭按钮/Esc 收起；断点回桌面自动关抽屉，无孤儿浮层。
 */
export class OASSidebar extends OASElement {
  static override get observedAttributes(): string[] {
    return [
      'collapsed',
      'accordion',
      'items',
      'width',
      'mobile-breakpoint',
      'active',
      'drawer-open',
      'hide-toggle',
      'loading',
      'expand-on-hover',
      'shortcut',
      'side',
      'variant',
      'resizable',
      'resize-min',
      'resize-max',
    ]
  }

  private _items: SidebarEntry[] = []

  /** Vue/React 会把 items 识别为实例属性走 property 赋值；setter 反射到 attribute 统一解析链路 */
  get items(): SidebarEntry[] {
    return this._items
  }
  set items(value: SidebarEntry[] | string) {
    this.setAttribute('items', typeof value === 'string' ? value : JSON.stringify(value))
  }
  private mq: MediaQueryList | null = null
  /** 展开的嵌套父项 value 集合（父项点击切换） */
  private expanded = new Set<string>()
  /** 嵌套父项含激活子项时自动展开（记录已自动展开的，避免渲染循环反复重置用户手动操作） */
  private autoExpanded = new Set<string>()
  /** 子元素通道观察器：light DOM 里 oas-sidebar-item/divider 增删或属性/文本变化 → 重解析渲染 */
  private childObserver: MutationObserver | null = null

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    return `
      <style>${STYLE}</style>
      <aside part="root">
        <div class="panel" part="panel">
          <div class="head" part="head">
            <slot name="header"></slot>
            <button part="close" type="button" hidden>✕</button>
          </div>
          <nav class="nav" part="nav"></nav>
          <div class="body" part="body"><slot></slot></div>
          <div class="foot" part="foot"><slot name="footer"></slot></div>
        </div>
        <button part="toggle" type="button" aria-expanded="true"></button>
        <div part="rail" role="separator" aria-orientation="vertical" tabindex="0" hidden></div>
      </aside>
      <button part="trigger" type="button" hidden><svg viewBox="0 0 16 16" width="1.25em" height="1.25em" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M3 4.5 H13 M3 8 H13 M3 11.5 H13"/></svg></button>
      <div class="mask" part="mask"></div>
    `
  }

  /** 缓存节点引用 + 绑定事件 + 注册清理（render 与水合路径共用） */
  private bind(): void {
    this.shadow.querySelector('.mask')?.addEventListener('click', () => this.closeDrawer())
    this.shadow.querySelector('[part="close"]')?.addEventListener('click', () => this.closeDrawer())
    this.shadow.querySelector('[part="trigger"]')?.addEventListener('click', () => this.openDrawer())
    this.shadow.querySelector('[part="toggle"]')?.addEventListener('click', () => this.toggleCollapsed())

    document.addEventListener('keydown', this.handleDocKeydown)
    this.onCleanup(() => document.removeEventListener('keydown', this.handleDocKeydown))

    // flyout 外部点击关闭（pointerdown 在 shadow 外落到别处即关）
    document.addEventListener('pointerdown', this.handleDocPointerDown, true)
    this.onCleanup(() => document.removeEventListener('pointerdown', this.handleDocPointerDown, true))

    // flyout 打开期间页面滚动/缩放：关闭（fixed 定位脱锚，对齐浮层组件 close-on-scroll 惯例）；
    // 组件内部滚动（.nav 滚动 / flyout 自身超长滚动）不关——锚点未位移，仅放行内部滚动源
    window.addEventListener('scroll', this.handleViewportScrollResize, true)
    window.addEventListener('resize', this.handleViewportScrollResize)
    this.onCleanup(() => {
      window.removeEventListener('scroll', this.handleViewportScrollResize, true)
      window.removeEventListener('resize', this.handleViewportScrollResize)
    })

    // 菜单键盘导航：↑/↓ 在可见项间移动焦点（Home/End 跳首末；Enter/Space 走原生 button 激活）
    const nav = this.shadow.querySelector('.nav')
    nav?.addEventListener('keydown', (e) => this.onNavKey(e))

    // 边缘拖拽条（resizable）：pointer 拖拽调宽 + 方向键微调
    const rail = this.shadow.querySelector<HTMLElement>('[part="rail"]')
    rail?.addEventListener('pointerdown', (e) => this.startRailDrag(e))
    rail?.addEventListener('keydown', (e) => this.onRailKey(e))
    this.syncRailAria()

    this.syncMq()
    this.onCleanup(() => this.mq?.removeEventListener('change', this.mqListener))
  }

  /**
   * document keydown：Esc 逐层退出（折叠态 flyout 优先于抽屉关闭）+ ctrl/cmd+b 折叠切换
   * （仅 shortcut 属性开启时，避免默认劫持全局键）。类字段引用：addEventListener 规范
   * 去重 + onReconnect 幂等重挂。
   */
  private handleDocKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      // 折叠态 flyout 优先于抽屉关闭（Esc 逐层退出）
      if (this.flyoutOpen !== null) {
        this.closeAllFlyouts(true)
        return
      }
      this.closeDrawer()
    }
    if (
      this.hasAttr('shortcut') &&
      (e.ctrlKey || e.metaKey) &&
      !e.shiftKey &&
      !e.altKey &&
      e.key.toLowerCase() === 'b'
    ) {
      e.preventDefault()
      this.toggleCollapsed()
    }
  }

  /** flyout 外部点击关闭（pointerdown 在 shadow 外落到别处即关）——类字段引用（重连幂等重挂） */
  private handleDocPointerDown = (e: PointerEvent): void => {
    if (this.flyoutOpen === null) return
    const path = e.composedPath()
    if (path.includes(this)) return
    this.closeAllFlyouts(false)
  }

  /** flyout 打开期间页面滚动/缩放关闭——类字段引用（重连幂等重挂）；组件内部滚动不关 */
  private handleViewportScrollResize = (e?: Event): void => {
    if (this.flyoutOpen === null) return
    if (e && e.type === 'scroll' && e.composedPath().includes(this)) return
    this.closeAllFlyouts(false)
  }

  /** 断开重连重绑（core onReconnect 钩子）：document keydown/pointerdown 与 window
      scroll/resize 监听 cleanup 摘除后恢复（类字段引用，重挂不叠）——Esc 退出、
      外点关闭、滚动脱锚关闭与 ctrl/cmd+b 快捷键重连不失效 */
  protected override onReconnect(): void {
    document.addEventListener('keydown', this.handleDocKeydown)
    document.addEventListener('pointerdown', this.handleDocPointerDown, true)
    window.addEventListener('scroll', this.handleViewportScrollResize, true)
    window.addEventListener('resize', this.handleViewportScrollResize)
    // mq change 监听恢复（cleanup 摘除后 syncMq 的 media 未变 early-return 不会重挂——
    // 直接补挂（mq 实例在断开期未变，change 回调恢复；媒体变化时 update 的 syncMq 会重建）
    this.mq?.addEventListener('change', this.mqListener)
  }

  /** rail 拖拽：以 width 属性为唯一事实源（update 会写入 CSS 变量，不冲突） */
  private railDragging = false
  private railStartX = 0
  private railStartWidth = 0

  private resizeMin(): number {
    return Number(this.getAttr('resize-min', '160')) || 160
  }
  private resizeMax(): number {
    return Number(this.getAttr('resize-max', '480')) || 480
  }

  private startRailDrag(e: Event): void {
    const pe = e as PointerEvent
    if (pe.button !== 0 && pe.pointerType !== 'touch') return
    e.preventDefault()
    this.railDragging = true
    this.railStartX = pe.clientX
    this.railStartWidth = this.getBoundingClientRect().width
    this.setAttribute('data-resizing', '')
    document.addEventListener('pointermove', this.onRailDrag)
    document.addEventListener('pointerup', this.endRailDrag, { once: true })
  }

  private onRailDrag = (e: PointerEvent): void => {
    if (!this.railDragging) return
    // side=right（行内末端）的侧栏向行内起点拖才变宽（delta 取反）；RTL 下 rail 视觉换边，再取反
    const sign = (this.getAttr('side') === 'right' ? -1 : 1) * (isRtl(this) ? -1 : 1)
    const next = this.railStartWidth + sign * (e.clientX - this.railStartX)
    this.setWidthPx(next)
  }

  private endRailDrag = (): void => {
    if (!this.railDragging) return
    this.railDragging = false
    this.removeAttribute('data-resizing')
    document.removeEventListener('pointermove', this.onRailDrag)
    this.emit('resize', { width: this.currentWidthPx() })
  }

  /** 当前宽度 px（width 属性优先，回落实际盒宽） */
  private currentWidthPx(): number {
    const fromAttr = parseInt(this.getAttr('width', '0'), 10)
    return Number.isFinite(fromAttr) && fromAttr > 0 ? fromAttr : this.getBoundingClientRect().width
  }

  /** 方向键微调宽度（±8px；Home/End 跳最小/最大）；RTL 下 rail 视觉换边，方向语义随之镜像 */
  private onRailKey(e: Event): void {
    const ke = e as KeyboardEvent
    const step = 8
    const sign = (this.getAttr('side') === 'right' ? -1 : 1) * (isRtl(this) ? -1 : 1)
    const cur = this.currentWidthPx()
    if (ke.key === 'ArrowRight') {
      ke.preventDefault()
      this.setWidthPx(cur + sign * step)
    } else if (ke.key === 'ArrowLeft') {
      ke.preventDefault()
      this.setWidthPx(cur - sign * step)
    } else if (ke.key === 'Home') {
      ke.preventDefault()
      this.setWidthPx(this.resizeMin())
    } else if (ke.key === 'End') {
      ke.preventDefault()
      this.setWidthPx(this.resizeMax())
    } else {
      return
    }
    this.emit('resize', { width: this.currentWidthPx() })
  }

  private setWidthPx(px: number): void {
    const clamped = Math.round(Math.min(this.resizeMax(), Math.max(this.resizeMin(), px)))
    this.setAttribute('width', `${clamped}px`)
    this.syncRailAria(clamped)
  }

  /** 可聚焦 separator 的 aria 值三件套（当前宽度 px），随拖拽/方向键同步 */
  private syncRailAria(value?: number): void {
    const rail = this.shadow.querySelector<HTMLElement>('[part="rail"]')
    if (!rail) return
    const v = value ?? Math.round(this.currentWidthPx())
    rail.setAttribute('aria-valuemin', String(this.resizeMin()))
    rail.setAttribute('aria-valuemax', String(this.resizeMax()))
    rail.setAttribute('aria-valuenow', String(v))
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（panel 存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('.panel')) return false
    this.bind()
    return true
  }

  /** matchMedia 变化：断点穿越时重新判定（抽屉关闭逻辑收敛在 update） */
  private mqListener = (): void => {
    this.update()
  }

  private syncMq(): void {
    const bp = Number(this.getAttr('mobile-breakpoint', String(DEFAULT_BREAKPOINT))) || DEFAULT_BREAKPOINT
    const media = `(max-width: ${bp}px)`
    if (this.mq && this.mq.media === media) return
    this.mq?.removeEventListener('change', this.mqListener)
    this.mq = window.matchMedia?.(media) ?? null
    this.mq?.addEventListener('change', this.mqListener)
  }

  /** 菜单键盘导航：ArrowDown/Up 移动焦点、Home/End 跳首末 */
  private onNavKey(e: Event): void {
    const ke = e as KeyboardEvent
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(ke.key)) return
    const items = [...this.shadow.querySelectorAll<HTMLElement>('.nav [part="item"]')].filter((b) => !b.hidden)
    if (!items.length) return
    e.preventDefault()
    const activeEl = this.shadow.activeElement as HTMLElement | null
    const idx = activeEl ? items.indexOf(activeEl) : -1
    let next = 0
    if (ke.key === 'ArrowDown') next = idx < 0 ? 0 : (idx + 1) % items.length
    else if (ke.key === 'ArrowUp') next = idx < 0 ? items.length - 1 : (idx - 1 + items.length) % items.length
    else if (ke.key === 'Home') next = 0
    else next = items.length - 1
    items[next]?.focus()
  }

  protected override update(): void {
    // RTL 书写方向标记（移动抽屉滑入方向/阴影投影/rail 拖拽语义镜像用；见 shared/direction 消费约定）
    this.toggleAttribute('data-rtl', isRtl(this))
    // 子元素通道观察器（重连后重建；items 属性显式时子元素被忽略，观察器空转无副作用）
    this.ensureChildObserver()
    this.syncMq()
    const mobile = this.mq?.matches ?? false
    // 桌面态不允许残留抽屉浮层（防孤儿）
    if (!mobile && this.hasAttr('drawer-open')) this.removeAttribute('drawer-open')
    const drawerOpen = mobile && this.hasAttr('drawer-open')

    if (mobile) this.dataset.mobile = 'true'
    else delete this.dataset.mobile

    const panel = this.shadow.querySelector<HTMLElement>('.panel')
    if (panel) {
      panel.classList.toggle('drawer-open', drawerOpen)
      // 仅移动端做 aria-hidden（桌面面板常驻可见，不能隐藏）
      if (mobile) panel.setAttribute('aria-hidden', String(!drawerOpen))
      else panel.removeAttribute('aria-hidden')
    }
    this.shadow.querySelector<HTMLElement>('.mask')?.classList.toggle('open', drawerOpen)

    const toggle = this.shadow.querySelector<HTMLElement>('[part="toggle"]')
    const trigger = this.shadow.querySelector<HTMLElement>('[part="trigger"]')
    const close = this.shadow.querySelector<HTMLElement>('[part="close"]')
    if (toggle) toggle.hidden = mobile || this.hasAttr('hide-toggle')
    if (trigger) trigger.hidden = !mobile
    if (close) close.hidden = !drawerOpen

    // 内置文案走 locale registry（setLocale 切换自动重刷 update）
    this.shadow.querySelector<HTMLElement>('.nav')?.setAttribute('aria-label', this.t('sidebar.nav'))
    if (toggle) {
      const collapsed = this.hasAttr('collapsed')
      toggle.setAttribute('aria-expanded', String(!collapsed))
      toggle.setAttribute('aria-label', collapsed ? this.t('sidebar.expand') : this.t('sidebar.toggle'))
      // 箭头语义 = 折叠图标条的展开方向（视觉）：RTL 下视觉换边，字符随之镜像
      const rtl = isRtl(this)
      toggle.textContent = collapsed ? (rtl ? '«' : '»') : rtl ? '»' : '«'
    }
    if (trigger) trigger.setAttribute('aria-label', this.t('sidebar.openMenu'))
    if (close) close.setAttribute('aria-label', this.t('sidebar.closeMenu'))

    // 边缘拖拽条：仅桌面 + resizable + 非折叠时显示；aria-label 走 locale
    const rail = this.shadow.querySelector<HTMLElement>('[part="rail"]')
    if (rail) {
      const showRail = this.hasAttr('resizable') && !mobile && !this.hasAttr('collapsed')
      rail.hidden = !showRail
      if (showRail) rail.setAttribute('aria-label', this.t('sidebar.resize'))
    }

    // width 属性 → 覆盖展开宽度 token
    const width = this.getAttr('width')
    if (width) this.style.setProperty('--oas-sidebar-width', width)
    else this.style.removeProperty('--oas-sidebar-width')

    // 移动端抽屉始终展示完整项；桌面折叠态只保留图标项
    this.renderItems(this.hasAttr('collapsed') && !mobile)

    // rail aria 三件套随 width/resize-min/max 属性变更同步（bind 早期 rect 可能为 0，也不得残留）
    this.syncRailAria()
  }

  private renderItems(collapsed: boolean): void {
    const nav = this.shadow.querySelector<HTMLElement>('.nav')
    if (!nav) return
    // flyout 状态重置：nav 重建时 flyout DOM 同步销毁，flyoutOpen 不得残留旧值
    // （否则宿主改 active/items 后，同一父项首次点击被判「再点关闭」空转一次）
    this.flyoutOpen = null
    this.clearFlyoutTimers()
    // 双通道：items 属性显式设置时数据驱动优先；否则解析子元素收敛到同一 items 模型渲染
    if (this.hasAttribute('items')) this.parseItems()
    else this.parseChildItems()
    nav.innerHTML = ''
    // loading 骨架屏：渲染脉冲骨架行，不渲染菜单
    if (this.hasAttr('loading')) {
      const n = Math.max(1, Number(this.getAttr('loading', '4')) || 4)
      for (let i = 0; i < n; i++) {
        const row = document.createElement('div')
        row.className = 'skeleton'
        row.setAttribute('part', 'skeleton')
        row.setAttribute('aria-hidden', 'true')
        nav.appendChild(row)
      }
      return
    }
    const active = this.getAttr('active', '')
    let currentGroup: string | undefined
    for (const entry of this._items) {
      if ('type' in entry && entry.type === 'divider') {
        currentGroup = undefined
        const divider = document.createElement('div')
        divider.className = 'divider'
        divider.setAttribute('part', 'divider')
        divider.setAttribute('role', 'separator')
        nav.appendChild(divider)
        continue
      }
      const item = entry as SidebarItem
      const group = item.group?.trim()
      // 组首项前渲染组标题（纯展示、part=group；无 group 或换组时插入）
      if (group && group !== currentGroup) {
        const title = document.createElement('div')
        title.className = 'group-title'
        title.setAttribute('part', 'group')
        title.textContent = group
        nav.appendChild(title)
      }
      currentGroup = group || undefined
      nav.appendChild(this.renderItem(item, collapsed, active, 0))
    }
  }

  /** 渲染单个菜单项（含徽标/操作/嵌套子项；collapsed=桌面图标条态） */
  private renderItem(item: SidebarItem, collapsed: boolean, active: string, depth: number): HTMLElement {
    const hasChildren = !!item.children?.length
    // 折叠图标条态 + 树形父项：走 flyout 子菜单（父项不是页面，点击开合面板——
    // 此前曾把父项点击退化为派发 select 修「死交互」，但父项 value 不是可导航页面，
    // 用户侧仍无任何可达子页的路径；flyout 才是主流侧栏惯例）
    const flyoutMode = collapsed && hasChildren && !!item.icon
    const effectiveHasChildren = hasChildren && !collapsed
    const btn = document.createElement('button')
    btn.className = 'item'
    btn.setAttribute('part', 'item')
    btn.type = 'button'
    btn.setAttribute('aria-label', item.label)
    btn.dataset.value = item.value
    if (depth > 0) btn.classList.add('sub')
    const isActive = !hasChildren && item.value === active
    if (isActive) {
      btn.classList.add('active')
      btn.setAttribute('aria-current', 'page')
    }
    // 激活后代指示：父项（含折叠态纯图标项）带 child-selected 主色淡底——
    // 折叠图标条下子项隐身，激活态经父项保留可见（父项非当前页，不设 aria-current）
    if (hasChildren && active && this.findActiveChild(item.children!, active)) {
      btn.classList.add('child-selected')
    }
    const icon = document.createElement('span')
    icon.className = 'icon'
    const iconSvg = item.icon ? this.iconSvg(item.icon, item.iconColor) : null
    if (iconSvg) {
      icon.innerHTML = iconSvg
    } else if (item.icon) {
      // 非注册表名下的自定义图标名：回退文本（不静默丢失）
      icon.textContent = item.icon
    } else if (depth > 0) {
      // 嵌套无图标项保留图标占位（保持子项文字缩进对齐父项 label 右侧，避免与父项齐平/更靠左）
      icon.setAttribute('aria-hidden', 'true')
    } else {
      icon.hidden = true
    }
    const label = document.createElement('span')
    label.className = 'label'
    label.textContent = item.label
    btn.append(icon, label)
    // 徽标：项右侧小圆角计数
    if (item.badge != null && item.badge !== '') {
      const badge = document.createElement('span')
      badge.className = 'item-badge'
      badge.setAttribute('part', 'badge')
      badge.textContent = String(item.badge)
      btn.appendChild(badge)
    }
    // 嵌套父项：右侧展开箭头；点击切换展开（不派发 select）。
    // 折叠图标条态（effectiveHasChildren=false）跳过此分支，走下方普通项点击
    let subWrap: HTMLElement | null = null
    if (effectiveHasChildren) {
      // 含激活子项的父项自动展开（一次，不打断用户后续手动收起）
      const hasActiveChild = this.findActiveChild(item.children!, active)
      if (hasActiveChild && !this.autoExpanded.has(item.value)) {
        this.expanded.add(item.value)
        this.autoExpanded.add(item.value)
      }
      const expanded = this.expanded.has(item.value)
      btn.setAttribute('aria-expanded', String(expanded))
      const chevron = document.createElement('span')
      chevron.className = 'chevron'
      chevron.setAttribute('aria-hidden', 'true')
      chevron.innerHTML = this.chevronSvg()
      btn.appendChild(chevron)
      subWrap = document.createElement('div')
      subWrap.className = 'submenu'
      subWrap.setAttribute('part', 'submenu')
      subWrap.hidden = !expanded
      // grid 高度动画的必需结构：内层 overflow 裁剪（0fr 行高裁剪内容）
      const subInner = document.createElement('div')
      subInner.className = 'submenu-inner'
      subWrap.appendChild(subInner)
      for (const child of item.children!) {
        if ('type' in child && (child as SidebarDivider).type === 'divider') {
          const d = document.createElement('div')
          d.className = 'divider'
          d.setAttribute('part', 'divider')
          d.setAttribute('role', 'separator')
          subInner.appendChild(d)
        } else {
          subInner.appendChild(this.renderItem(child as SidebarItem, collapsed, active, depth + 1))
        }
      }
      btn.addEventListener('click', () => {
        if (this.expanded.has(item.value)) {
          this.expanded.delete(item.value)
        } else {
          // accordion 同级互斥（与 menu 同语义）：展开前收起同级的其他展开父项。
          // 不做全量 update（重渲染会让新 submenu 生而展开、过渡无初态可播）——
          // 外科手术式同步被收起项的 DOM（同元素 hidden/aria 切换，双方过渡都能播）
          if (this.hasAttr('accordion')) {
            for (const sib of this.siblingValuesOf(item.value)) {
              if (!this.expanded.has(sib)) continue
              this.expanded.delete(sib)
              const sibBtn = this.shadow.querySelector<HTMLElement>(`[part="item"][data-value="${sib}"]`)
              if (sibBtn) {
                sibBtn.setAttribute('aria-expanded', 'false')
                const sibSub = sibBtn.closest('.item-block')?.querySelector<HTMLElement>('[part="submenu"]')
                if (sibSub) sibSub.hidden = true
              }
            }
          }
          this.expanded.add(item.value)
        }
        btn.setAttribute('aria-expanded', String(this.expanded.has(item.value)))
        subWrap!.hidden = !this.expanded.has(item.value)
      })
    } else if (!flyoutMode) {
      btn.addEventListener('click', () => {
        this.emit('select', { value: item.value, label: item.label })
        if (this.hasAttr('drawer-open')) this.closeDrawer()
      })
    }
    // 项操作按钮：悬停项时出现；点击派发 oas-action（不触发 select/展开）
    if (item.actions?.length) {
      const actions = document.createElement('span')
      actions.className = 'item-actions'
      for (const action of item.actions) {
        const actionBtn = document.createElement('button')
        actionBtn.className = 'item-action'
        actionBtn.setAttribute('part', 'action')
        actionBtn.type = 'button'
        actionBtn.setAttribute('aria-label', action.label)
        const aSvg = action.icon ? this.iconSvg(action.icon) : null
        if (aSvg) actionBtn.innerHTML = aSvg
        else actionBtn.textContent = action.label
        actionBtn.addEventListener('click', (e) => {
          e.stopPropagation()
          this.emit('action', { value: item.value, action: action.value, label: action.label })
        })
        actions.appendChild(actionBtn)
      }
      btn.appendChild(actions)
    }
    // 折叠态无图标项整体隐藏（仅保留图标）；嵌套子项在折叠态隐藏
    if (collapsed && !item.icon) btn.hidden = true
    // 折叠态 + 树形父项：flyout 子菜单（父项不是页面——点击开合面板；子项才是导航目标）
    if (collapsed && hasChildren && item.icon) {
      const block = document.createElement('div')
      block.className = 'item-block'
      const flyout = this.buildFlyout(item, active)
      block.append(btn, flyout)
      btn.setAttribute('aria-haspopup', 'true')
      btn.setAttribute('aria-expanded', 'false')
      btn.addEventListener('click', () => this.toggleFlyout(item.value, btn, flyout))
      // hover 延迟开合（宽限防掠过抖动）
      btn.addEventListener('pointerenter', () => this.scheduleFlyoutOpen(item.value, btn, flyout))
      block.addEventListener('pointerleave', () => this.scheduleFlyoutClose(flyout))
      // 键盘：Enter/Space 经 button 原生 click 已覆盖；Esc 在组级 keydown（bind 时挂）
      return block
    }
    // 折叠态：图标项包 tooltip（label 提示，placement=right）
    let out: HTMLElement = btn
    if (collapsed && item.icon && !hasChildren) {
      const tip = document.createElement('oas-tooltip')
      tip.setAttribute('content', item.label)
      tip.setAttribute('placement', 'right')
      tip.appendChild(btn)
      out = tip as unknown as HTMLElement
    }
    if (subWrap) {
      const frag = document.createElement('div')
      frag.className = 'item-block'
      frag.append(out, subWrap)
      // 折叠态嵌套父项的子树隐藏
      if (collapsed) subWrap.hidden = true
      return frag
    }
    return out
  }

  /** 递归查找激活子项（用于父项自动展开） */
  private findActiveChild(children: SidebarEntry[], active: string): boolean {
    for (const c of children) {
      if ('type' in c && c.type === 'divider') continue
      const item = c as SidebarItem
      if (item.value === active) return true
      if (item.children && this.findActiveChild(item.children, active)) return true
    }
    return false
  }

  // ===== 折叠图标条态的子菜单 flyout（collapsed × 树形 children）=====

  /** 当前打开的 flyout 父项 value（单开互斥） */
  private flyoutOpen: string | null = null
  private flyoutHoverTimer: ReturnType<typeof setTimeout> | null = null
  private flyoutLeaveTimer: ReturnType<typeof setTimeout> | null = null
  /** hover 延迟开合阈值（ms）：开 150 防掠过误开、关 300 留移向面板的宽限 */
  private static readonly FLYOUT_OPEN_DELAY = 150
  private static readonly FLYOUT_CLOSE_DELAY = 300

  /** 构建 flyout 面板：子项按展开态渲染（嵌套子树内联可展开），叶子子项点击派发 select 后关面板 */
  private buildFlyout(item: SidebarItem, active: string): HTMLElement {
    const flyout = document.createElement('div')
    flyout.className = 'flyout'
    flyout.setAttribute('part', 'flyout')
    flyout.setAttribute('role', 'menu')
    flyout.dataset.parent = item.value
    flyout.hidden = true
    for (const child of item.children!) {
      if ('type' in child && (child as SidebarDivider).type === 'divider') {
        const d = document.createElement('div')
        d.className = 'divider'
        d.setAttribute('part', 'divider')
        d.setAttribute('role', 'separator')
        flyout.appendChild(d)
      } else {
        const node = this.renderItem(child as SidebarItem, false, active, 1)
        // flyout 内所有菜单按钮挂 menuitem 语义（根节点 + 嵌套子树内按钮）
        if (node.matches?.('[part="item"]')) node.setAttribute('role', 'menuitem')
        for (const btn of node.querySelectorAll<HTMLElement>('[part="item"]')) btn.setAttribute('role', 'menuitem')
        flyout.appendChild(node)
      }
    }
    // 叶子子项点击后关闭面板（select 已由子项自身派发）；嵌套父项的内联展开不关面板
    flyout.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest?.('[part="item"]') as HTMLElement | null
      if (!btn) return
      if (btn.querySelector('.chevron')) return // 嵌套父项：内联展开，不关面板
      this.closeAllFlyouts(false)
    })
    // hover 链路关键补丁：指针进入面板时取消一切开合计时——btn↔flyout 之间有定位 gap，
    // 穿越 gap 会触发 block 的 pointerleave（关闭计时已启动），flyout 自身若无 pointerenter
    // 处理器，面板会在指针已在面板内悬停时仍于 300ms 后被关闭（实抓 hover 链路断裂）
    flyout.addEventListener('pointerenter', () => this.clearFlyoutTimers())
    return flyout
  }

  /** 开合 flyout：单开互斥 + 定位引擎锚定父项 inline-end（RTL 镜像）+ 碰撞避让 */
  private toggleFlyout(value: string, btn: HTMLElement, flyout: HTMLElement): void {
    if (this.flyoutOpen === value) {
      this.closeAllFlyouts(true)
      return
    }
    this.closeAllFlyouts(false)
    flyout.hidden = false
    btn.setAttribute('aria-expanded', 'true')
    this.flyoutOpen = value
    this.positionFlyout(btn, flyout)
  }

  private positionFlyout(btn: HTMLElement, flyout: HTMLElement): void {
    const anchorRect = btn.getBoundingClientRect()
    const popupRect = flyout.getBoundingClientRect()
    const { top, left, placement } = computePosition(anchorRect, popupRect, 'right-start', getViewport(), 4, true, {
      collisionPadding: 8,
      direction: isRtl(this) ? 'rtl' : 'ltr',
    })
    flyout.style.top = `${top}px`
    flyout.style.left = `${left}px`
    flyout.dataset.placement = placement
  }

  /** 关闭全部 flyout；refocus=true 时焦点回到父项（Esc 路径） */
  private closeAllFlyouts(refocus: boolean): void {
    const openValue = this.flyoutOpen
    this.flyoutOpen = null
    this.clearFlyoutTimers()
    for (const flyout of this.shadow.querySelectorAll<HTMLElement>('[part="flyout"]')) {
      flyout.hidden = true
    }
    for (const btn of this.shadow.querySelectorAll<HTMLElement>('[part="item"][aria-haspopup="true"]')) {
      btn.setAttribute('aria-expanded', 'false')
      if (refocus && openValue !== null && btn.dataset.value === openValue) btn.focus()
    }
  }

  private clearFlyoutTimers(): void {
    if (this.flyoutHoverTimer) clearTimeout(this.flyoutHoverTimer)
    if (this.flyoutLeaveTimer) clearTimeout(this.flyoutLeaveTimer)
    this.flyoutHoverTimer = null
    this.flyoutLeaveTimer = null
  }

  private scheduleFlyoutOpen(value: string, btn: HTMLElement, flyout: HTMLElement): void {
    this.clearFlyoutTimers()
    this.flyoutHoverTimer = setTimeout(() => {
      if (this.flyoutOpen !== value) this.toggleFlyout(value, btn, flyout)
    }, OASSidebar.FLYOUT_OPEN_DELAY)
  }

  private scheduleFlyoutClose(flyout: HTMLElement): void {
    this.clearFlyoutTimers()
    this.flyoutLeaveTimer = setTimeout(() => {
      flyout.hidden = true
      this.flyoutOpen = null
      const parent = flyout.parentElement?.querySelector<HTMLElement>('[part="item"][aria-haspopup="true"]')
      parent?.setAttribute('aria-expanded', 'false')
    }, OASSidebar.FLYOUT_CLOSE_DELAY)
  }

  /** accordion 同级互斥：返回与 value 同级（同一父数组内）的其他可展开父项 value */
  private siblingValuesOf(value: string): string[] {
    const findSiblings = (entries: SidebarEntry[]): string[] | null => {
      const values = entries.filter((e): e is SidebarItem => !('type' in e)).map((i) => i.value)
      if (values.includes(value)) {
        return entries
          .filter((e): e is SidebarItem => !('type' in e))
          .filter((i) => i.value !== value && !!i.children?.length)
          .map((i) => i.value)
      }
      for (const e of entries) {
        if ('type' in e) continue
        const item = e as SidebarItem
        if (item.children) {
          const found = findSiblings(item.children)
          if (found) return found
        }
      }
      return null
    }
    return findSiblings(this._items) ?? []
  }

  private chevronSvg(): string {
    return `<svg viewBox="0 0 16 16" width="0.9em" height="0.9em" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4.5 L10 8 L6 11.5"/></svg>`
  }

  /** 图标名（查表：registerIcon 自定义优先，其次内置注册表）→ 内联 SVG。
   *  iconColor 显式时固定该色（优先于禁用/激活态默认色）；缺省 currentColor 随态着色。
   *  内置单色 path 自带 stroke="currentColor"——iconColor 显式时需把 path 的 currentColor 也替换，
   *  否则 path 元素级属性压过 svg 外层 stroke，颜色永远 currentColor（内置图标配 iconColor 场景）；
   *  自定义注册的彩色 SVG（path 自带具体色 stroke/fill）天然兼容，无需替换 */
  private iconSvg(name: string, iconColor?: string): string | null {
    const path = lookupIcon(name)
    if (!path) return null
    const stroke = iconColor || 'currentColor'
    const coloredPath = iconColor ? path.replace(/stroke="currentColor"/g, `stroke="${stroke}"`) : path
    return `<svg viewBox="0 0 16 16" width="1.25em" height="1.25em" aria-hidden="true" focusable="false" fill="none" stroke="${stroke}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${coloredPath}</svg>`
  }

  private parseItems(): void {
    try {
      const parsed = JSON.parse(this.getAttr('items', '[]'))
      this._items = Array.isArray(parsed)
        ? parsed.map((e): SidebarEntry | null => this.parseEntry(e)).filter((e): e is SidebarEntry => e !== null)
        : []
    } catch {
      this._items = []
    }
  }

  /** 递归解析条目：divider 直通；item 校验 label/value，children 递归校验 */
  private parseEntry(e: unknown): SidebarEntry | null {
    if (!e || typeof e !== 'object') return null
    const entry = e as Record<string, unknown>
    if (entry.type === 'divider') return { type: 'divider' }
    if (typeof entry.label !== 'string' || typeof entry.value !== 'string') return null
    const item: SidebarItem = {
      label: entry.label,
      value: entry.value,
      icon: typeof entry.icon === 'string' ? entry.icon : undefined,
      iconColor: typeof entry.iconColor === 'string' ? entry.iconColor : undefined,
      group: typeof entry.group === 'string' ? entry.group : undefined,
      badge:
        typeof entry.badge === 'string' || typeof entry.badge === 'number'
          ? (entry.badge as string | number)
          : undefined,
      actions: Array.isArray(entry.actions)
        ? (entry.actions as unknown[])
            .filter(
              (a): a is SidebarItemAction =>
                !!a &&
                typeof (a as SidebarItemAction).value === 'string' &&
                typeof (a as SidebarItemAction).label === 'string',
            )
            .map((a) => ({ icon: a.icon, value: a.value, label: a.label }))
        : undefined,
      children: Array.isArray(entry.children)
        ? ((entry.children as unknown[])
            .map((c) => this.parseEntry(c))
            .filter((c): c is SidebarItem => c !== null && (c as SidebarItem).label !== undefined) as SidebarItem[])
        : undefined,
    }
    return item
  }

  // ===== 子元素声明式通道 =====

  /**
   * 子元素通道解析层：把 light DOM 的 `<oas-sidebar-item>` / `<oas-sidebar-divider>`
   * 收敛为内部 _items 模型（与 parseItems 单一渲染路径，renderItems 无感）。
   */
  private parseChildItems(): void {
    this._items = this.parseChildLevel(this.children)
  }

  /** 解析一层子元素为 SidebarEntry[]（仅识别数据载体元素，其余 light DOM 内容忽略） */
  private parseChildLevel(elements: HTMLCollection | Element[]): SidebarEntry[] {
    const items: SidebarEntry[] = []
    for (const child of Array.from(elements)) {
      if (child.tagName === 'OAS-SIDEBAR-ITEM') {
        items.push(this.childToSidebarItem(child))
      } else if (child.tagName === 'OAS-SIDEBAR-DIVIDER') {
        items.push({ type: 'divider' })
      }
    }
    return items
  }

  /** 单个 <oas-sidebar-item> → SidebarItem（默认插槽文本为 label，属性对齐 items 字段；
      嵌套子元素递归为 children。children 内分隔线仅子元素通道支持；items JSON 通道的
      children 不支持分隔线——parseEntry 会过滤无 label 条目） */
  private childToSidebarItem(el: Element): SidebarItem {
    const item: SidebarItem = {
      label: this.childLabel(el),
      value: el.getAttribute('value') ?? '',
    }
    const icon = el.getAttribute('icon')
    if (icon) item.icon = icon
    const iconColor = el.getAttribute('icon-color')
    if (iconColor) item.iconColor = iconColor
    const group = el.getAttribute('group')
    if (group) item.group = group
    const badge = el.getAttribute('badge')
    if (badge) {
      // 数字语义：纯数字字符串转 number，其余保留 string（对齐 SidebarItem.badge 的 string|number）
      item.badge = /^\d+$/.test(badge) ? Number(badge) : badge
    }
    const children = this.parseChildLevel(el.children)
    if (children.length > 0) item.children = children as SidebarItem[]
    return item
  }

  /** 默认插槽 label 文本：跳过嵌套数据载体元素（其文本属于子菜单而非 label） */
  private childLabel(el: Element): string {
    let text = ''
    for (const node of el.childNodes) {
      if (node instanceof Element) {
        const tag = node.tagName
        if (tag === 'OAS-SIDEBAR-ITEM' || tag === 'OAS-SIDEBAR-DIVIDER') continue
      }
      text += node.textContent ?? ''
    }
    return text.trim()
  }

  /**
   * 子元素通道观察器：只监听 light DOM 子元素（数据载体增删/属性/文本变化 → 重解析）。
   * 组件自身动作不写 light DOM，无需自引用守卫；挂 update() 首行（断开重连后重建，幂等）。
   */
  private ensureChildObserver(): void {
    if (this.childObserver) return
    const observer = new MutationObserver(() => {
      this.update()
    })
    observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
      attributeFilter: ['value', 'icon', 'icon-color', 'group', 'badge'],
    })
    this.childObserver = observer
    this.onCleanup(() => {
      observer.disconnect()
      this.childObserver = null
    })
  }

  /** 桌面折叠切换：受控属性 + 派发事件供宿主响应 */
  private toggleCollapsed(): void {
    const collapsed = this.hasAttr('collapsed')
    if (collapsed) this.removeAttribute('collapsed')
    else this.setAttribute('collapsed', '')
    this.emit('collapse', { collapsed: !collapsed })
    this.update()
  }

  /**
   * 公开：编程打开移动抽屉，等价于点击内置触发按钮（`part="trigger"`）。
   * 仅移动端生效：桌面态设置后由 update 立即回收，不残留浮层；已打开时幂等。
   * 写入 `drawer-open` 受控属性，宿主可观察/回写；不额外派发事件（沿用既有语义）。
   */
  public openDrawer(): void {
    if (this.hasAttr('drawer-open')) return
    this.setAttribute('drawer-open', '')
    this.update()
  }

  /**
   * 公开：编程关闭移动抽屉，等价于点击遮罩 / 关闭按钮 / 按 Esc。
   * 未打开时幂等；移除 `drawer-open` 受控属性，宿主可观察/回写。
   */
  public closeDrawer(): void {
    if (!this.hasAttr('drawer-open')) return
    this.removeAttribute('drawer-open')
    this.update()
  }
}
